// @workflow assistant.ui-tools, tasks.show-in-3d
/**
 * Link chips that point at a tag, or at a task's board (`?intent=task-board&taskId=…`), run the
 * action, they don't change the URL (Khalid, 2026-10-14; the board: AVA-753).
 *
 * The server builds tag links as deep links (aiva.api MODS.Forge.Mcp/Ava/AvaUiLinks.cs):
 * `/:facilityId?tagId=…&openPanel=true | isolate=true | zoom360=true`. The facility page reads those
 * only when it first loads (MainView `_urlHandlersExecuted`), so pushing one while the user is
 * already in that facility changed the address and did nothing. This reads the link's meaning in
 * one place and answers the UI action that does the same thing (uiTools.js); chipActions.js runs
 * it like any other button. The link itself stays for copying and sharing.
 *
 * TEMPORARY BRIDGE: remove once the server sends `action` on every tag chip
 * (contract "Buttons are actions").
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL } from './uiToolContract.js';
import { FOCUS_MODE } from './viewerContract.js';
import { ALL_TAG_TABS, TAG_TAB } from '../../Tags/utils/tagInfoTabs.js';
import { tagIdsFromQuery } from '../../../utils/tagDeepLink.js';
import { SHARE_INTENT_TYPE } from '../../../utils/shareIntents/intentTypes.js';

/**
 * The deep-link flag → the action, in MainView's precedence: show2d first (with it MainView runs only
 * `handleTagShow2DFromUrl`), then `handleTagSpatialNavigationFromUrl`'s zoom360, isolate, openPanel.
 */
const FLAG_ACTIONS = Object.freeze([
  ['show2d', (tagId, facilityId) => ({ name: UI_TOOL.TAGS_SHOW_IN_2D, args: { tagId, facilityId } })],
  ['zoom360', (tagId, facilityId) => ({ name: UI_TOOL.TAGS_OPEN_360, args: { tagId, facilityId } })],
  // `tagIds` (several tags, utils/tagDeepLink.js) when the link has it, else the one `tagId`.
  ['isolate', (tagId, facilityId, query) => ({ name: UI_TOOL.VIEWER_FOCUS_TAGS, args: { tagIds: tagIdsFromQuery(query).tagIds, mode: FOCUS_MODE.ISOLATE, facilityId } })],
  // `tab=<a card tab>` (Tags/utils/tagInfoTabs.js, e.g. AvaUiLinks.cs TagFindings): the card on that tab;
  // info or an unknown value opens it as usual.
  ['openPanel', (tagId, facilityId, query) => ({
    name: UI_TOOL.TAGS_OPEN_PANEL,
    args: query.tab !== TAG_TAB.INFO && ALL_TAG_TABS.includes(query.tab) ? { tagId, facilityId, tab: query.tab } : { tagId, facilityId },
  })],
]);

/** A flag MainView hands to another handler instead (a document): the link stays a link. */
const OTHER_HANDLER_FLAGS = Object.freeze(['openDocument']);

const FACILITY_PATH = /^\/([A-Za-z0-9_-]+)\/?$/;
/** The share-intent query a task's board link carries (AvaUiLinks.cs Task: `?intent=task-board&taskId=…`). */
const INTENT_PARAM = 'intent';
const TASK_ID_PARAM = 'taskId';
const PROBE_ORIGIN = 'https://ava.invalid';
const TRUE = 'true';

/**
 * The UI action a chip's `to` stands for, or null when the chip is a plain link.
 * @param {unknown} to an app path (already checked by toAppLocation) or a named route
 * @returns {{ name: string, args: object } | null}
 */
export function linkActionOf(to) {
  if (typeof to !== 'string' || !to.startsWith('/')) return null;
  let url;
  try {
    url = new URL(to, PROBE_ORIGIN);
  } catch (err) {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  const path = url.pathname.match(FACILITY_PATH);
  // A task's board link: the task opens on its board through the action; the board comes from the task.
  const taskId = url.searchParams.get(TASK_ID_PARAM);
  if (path && taskId && url.searchParams.get(INTENT_PARAM) === SHARE_INTENT_TYPE.TASK_BOARD) {
    return { name: UI_TOOL.TASKS_OPEN_ON_BOARD, args: { taskId, facilityId: path[1] } };
  }
  const tagId = url.searchParams.get('tagId');
  if (!path || !tagId) return null;
  if (OTHER_HANDLER_FLAGS.some((flag) => url.searchParams.get(flag) === TRUE)) return null;
  const found = FLAG_ACTIONS.find(([flag]) => url.searchParams.get(flag) === TRUE);
  return found ? found[1](tagId, path[1], Object.fromEntries(url.searchParams)) : null;
}
